/*
  ┌─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
  │ Constant.js                                                                                                     │
  └─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┘
*/
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/*
  ┌─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
  │ URL Cleaning Functions                                                                                           │
  └─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┘
*/

/**
 * Removes tracking parameters from product URLs
 * @param {string} url - The URL to clean
 * @returns {string} - The cleaned URL without tracking parameters
 */
function cleanProductUrl(url) {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url, window.location.origin);
    
    // Remove tracking parameters that Shopify adds
    const trackingParams = ['_pos', '_fid', '_ss', '_sid', '_p', '_psq', '_psc', '_psv'];
    
    trackingParams.forEach(param => {
      urlObj.searchParams.delete(param);
    });
    
    return urlObj.pathname + urlObj.search;
  } catch (error) {
    console.warn('Error cleaning URL:', error);
    return url;
  }
}

/*
  ┌─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
  │ PubSub                                                                                                          │
  └─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┘
*/

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName]
      .map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function decodeBase64HTML(string) {
  try {
    return decodeURIComponent(atob(string).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  } catch (e) {
    console.error(`🚀 || product-addon.js:96 || AddonProductModal || decodeBase64HTML || Error decoding base64 HTML:`, e);
    return "";
  }
}

String.prototype.formatCityName = function () {
  return this.replace(/^city_/, '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus?.focus();

  if (elementToFocus && elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    const barQuantity = document.querySelector('[data-id="barQuantity"]');
    const barInput = barQuantity?.querySelector("input");
    if (barInput) {
      barInput.addEventListener("input", (event) => {
        this.input.value = barInput.value;
        this.validateQtyRules();
      });
    }
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
    const barQuantity = document.querySelector('[data-id="barQuantity"]');
    const barInput = barQuantity?.querySelector("input");
    if (barInput) {
      barInput.value = this.input.value;
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    let barQuantity = document.querySelector('[data-id="barQuantity"]');
    let barInput = barQuantity?.querySelector("input");

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
        if (barQuantity) {
          barInput.value = this.input.value;
        }
      }
    } else {
      this.input.stepDown();
      if (barQuantity) {
        barInput.value = this.input.value;
      }
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    let barQuantity = document.querySelector('[data-id="barQuantity"]');
    let barMinus = barQuantity?.querySelector('[name="minus"]');
    let barPlus = barQuantity?.querySelector('[name="plus"]');
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus?.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
      barMinus?.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus?.classList.toggle("disabled", value >= max);
      barPlus?.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    // :not(.button-show-more) is added to the button selector to exclude the show more button from Closing the Filter Drawer
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.button-show-more)'
    ).forEach((button) => {
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });
    
    // Add toggle event listener to handle native details element behavior
    this.mainDetailsToggle.addEventListener('toggle', this.onDetailsToggle.bind(this));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  onDetailsToggle(event) {
    const detailsElement = event.target;
    if (detailsElement === this.mainDetailsToggle) {
      if (!detailsElement.open) {
        // Details element is being closed, ensure menu-opening class is removed
        detailsElement.classList.remove('menu-opening');
        // Also remove from all child details elements
        detailsElement.querySelectorAll('details').forEach((details) => {
          details.classList.remove('menu-opening');
        });
        // Remove submenu-open classes
        detailsElement.querySelectorAll('.submenu-open').forEach((submenu) => {
          submenu.classList.remove('submenu-open');
        });
        // Remove body classes
        document.body.classList.remove(`overflow-hidden`);
        document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
        // Remove header menu-open class if it exists
        const header = document.querySelector('.section-header');
        if (header) {
          header.classList.remove('menu-open');
        }
      }
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    document.body.classList.remove(`overflow-hidden`);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);

    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut(evt) {
    setTimeout(() => {
      const hasFacetsSummaryClass = evt.target.classList.contains('facets__summary');
      const hasMobileFacetsOpenClass = evt.target.classList.contains('mobile-facets__open-wrapper');

      if (!hasFacetsSummaryClass && !hasMobileFacetsOpenClass) {
        return;
      }

      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement) && !hasFacetsSummaryClass && !hasMobileFacetsOpenClass) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    document.body.classList.remove(`overflow-hidden`);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);

    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement?.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset = this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.closest('details').setAttribute('open', "");
    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header && document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  slideSwiper(targetInput = undefined) {
    if (!targetInput) return;
    
    // Get the selected variant ID from the input
    const selectedVariantId = targetInput.getAttribute("data-variant-id");
    
    // If we have a variant ID and the update function exists, update the swiper
    if (selectedVariantId && typeof window.updateSwiperWithVariantImages === 'function') {
      window.updateSwiperWithVariantImages(selectedVariantId);
    } else {
      // Fallback to original behavior
      const productInfo = targetInput.closest("product-info");
      const productSlider = productInfo.querySelector(".product-slider");
      const swiperSlides = [...productSlider.querySelectorAll(".main-slider .swiper-slide")];
      let idx = swiperSlides.findIndex((slide) => slide.getAttribute("data-id-image") === targetInput.getAttribute("data-id-image"));
      if (window.mainSwiper && idx !== -1) {
        window.mainSwiper.slideTo(idx);
      }
    }
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      let indexValue = event.target.getAttribute('index')
      if(indexValue){
        window.newVariantSelected(parseInt(indexValue))
      }
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
      this.slideSwiper(target);
    });
  }

  updateSelectionMetadata({ target }) {
   
    const { value, tagName } = target;
    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options).find((option) => option.getAttribute('selected')).removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target.closest('.product-form__input').querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty('--swatch-focal-point',target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset');
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }


  }

  getInputForEventTarget(target) {
    if (target.tagName === 'SELECT' && target.selectedOptions.length) {
      return target.selectedOptions[0];
    }
    return target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {

    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (typeof initialCurrencyChange === 'function') initialCurrencyChange();
        if (typeof updateFooterCount === 'function') updateFooterCount();
      })
      ;
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }
}

class ImageCache {
  constructor(images, waitForOtherResources = false, timeout = 15000) {
    this.images = images;
    this.waitForOtherResources = waitForOtherResources;
    this.timeout = timeout;
    this.loaded = false;

    if (this.images.length !== 0) {
      if (!ImageCache.cacheList) {
        ImageCache.cacheList = [];
      }

      if (!waitForOtherResources || document.readyState === 'complete') {
        this.cacheNow();
      } else {
        this.observeDocumentLoad();
      }

      this.observeImages();
    }
  }

  observeDocumentLoad() {
    let timer = setTimeout(() => this.cacheNow(), this.timeout);

    window.addEventListener('load', () => {
      clearTimeout(timer);
      this.cacheNow();
    });
  }

  observeImages() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.cacheNow();
          observer.disconnect(); // Stop observing after caching
        }
      });
    });

    this.images.forEach((imageSrc) => {
      const imgElement = document.querySelector(`img[src="${imageSrc}"]`);
      if (imgElement) {
        observer.observe(imgElement);
      }
    });
  }

  cacheNow() {
    if (!this.loaded) {
      this.loaded = true;
      this.images.forEach((src) => {
        const img = new Image();
        img.onload = img.onerror = img.onabort = () => {
              const index = ImageCache.cacheList.indexOf(img);
              if (index !== -1) {
                ImageCache.cacheList.splice(index, 1);
              }
            };
        ImageCache.cacheList.push(img);
        img.src = src;
      });
    }
  }
}

class VideoPlayer {
  constructor(element) {
    this.video = element.querySelector('video');
    this.playButton = element.querySelector('[data-id="play-button"]');
    this.mute = element.querySelector('[data-id="mute-button"]');
    this.unmute = element.querySelector('[data-id="unmute-button"]');

    // Check if controls and autoplay are true
    if (this.video.hasAttribute('controls') && this.video.hasAttribute('autoplay')) {
      this.playButton.style.display = 'none';
      this.mute.style.display = 'none';
      this.unmute.style.display = 'none';
    } else {
      this.playButton.addEventListener('click', this.togglePlay.bind(this));
      this.video.addEventListener('click', this.togglePlay.bind(this));
      this.mute.addEventListener('click', this.muteVideo.bind(this));
      this.unmute.addEventListener('click', this.unmuteVideo.bind(this));
    }

    this.video.addEventListener('ended', this.resetPlayButton.bind(this));

    // Lazy Load Video
    this.lazyLoadVideo();
  }

  togglePlay() {
    if (this.video.paused || this.video.ended) {
      this.play();
    } else {
      this.pause();
    }
  }

  play() {
    this.pauseAll();
    this.video.play();
    this.playButton.style.display = 'none';
  }

  pauseAll() {
    document.querySelectorAll('video-element').forEach((videoElement) => {
      videoElement.querySelector('video').pause();
      videoElement.querySelector('[data-id="play-button"]').style.display = 'block';
    });
  }

  pause() {
    this.video.pause();
    this.playButton.style.display = 'block';
  }

  resetPlayButton() {
    this.playButton.style.display = 'block';
  }

  muteVideo() {
    this.video.muted = true;
    this.mute.style.display = 'none';
    this.unmute.style.display = 'block';
  }

  unmuteVideo() {
    this.video.muted = false;
    this.mute.style.display = 'block';
    this.unmute.style.display = 'none';
  }

  lazyLoadVideo() {
    let lazyVideoObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((video) => {
        if (video.isIntersecting) {
          for (let source of video.target.children) {
            if (source.tagName === 'SOURCE') {
              video.target.src = source.dataset.src;
              source.src = source.dataset.src;
            }
          }
          video.target.classList.remove('lazy');
          lazyVideoObserver.unobserve(video.target);
        }
      });
    });
    lazyVideoObserver.observe(this.video);
  }
}

class Modal {
  constructor(id, options = {}) {
    let object = Object.assign(
      {
        content: '',
        onConfirm: null,
        onCancel: null,
        onClose: null,
      },
      options
    );

    this.id = id || Math.random().toString(36).substring(2, 9); // More readable random ID

    this.content = object.content;
    this.onConfirm = object.onConfirm;
    this.onCancel = object.onCancel;
    this.onClose = object.onClose;

    this.create();
    this.setupEventListeners();
  }

  /**
   * Create the modal element with custom elements
   */
  create() {
    // Create main modal container
    this.xModal = document.createElement('x-modal');
    this.xModal.setAttribute('id', `modal-${this.id}`);
    this.xModal.setAttribute('aria-modal', 'true');
    this.xModal.setAttribute('role', 'dialog');

    // Create backdrop
    this.xModalBackdrop = document.createElement('x-modal-backdrop');
    this.xModalBackdrop.setAttribute('id', `modal-backdrop-${this.id}`);

    // Append backdrop to modal
    this.xModal.appendChild(this.xModalBackdrop);

    // Add content
    if (typeof this.content === 'string') {
      const contentWrapper = document.createElement('x-modal-content');
      contentWrapper.innerHTML = this.content;
      this.xModal.appendChild(contentWrapper);
    } else if (this.content instanceof HTMLElement) {
      let modalContent = this.content.cloneNode(true);
      this.xModal.appendChild(modalContent);
    }

    // Add to the DOM
    document.body.appendChild(this.xModal);
    this.open();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Find buttons within the modal
    this.confirmBtns = this.xModal.querySelectorAll('.modal-confirm, [x-modal-confirm]');
    this.cancelBtns = this.xModal.querySelectorAll('.modal-cancel, [x-modal-cancel]');
    this.closeBtns = this.xModal.querySelectorAll('.modal-close, [x-modal-close]');

    // Close modal when clicking the close buttons
    if (this.closeBtns.length > 0) {
      this.closeBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          if (typeof this.onClose === 'function') {
            this.onClose(e);
          }
          this.close();
        });
      });
    }

    // Handle cancel buttons
    if (this.cancelBtns.length > 0) {
      this.cancelBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          if (typeof this.onCancel === 'function') {
            this.onCancel(e);
          }
          this.close();
        });
      });
    }

    // Handle confirm buttons
    if (this.confirmBtns.length > 0) {
      this.confirmBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          if (typeof this.onConfirm === 'function') {
            this.onConfirm(e);
          }
          this.close();
        });
      });
    }

    // Close modal when clicking the backdrop
    this.xModalBackdrop.addEventListener('click', (e) => {
      if (e.target === this.xModalBackdrop) {
        this.close();
      }
    });

    // Close modal when pressing Escape key
    this.escapeListener = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeListener);
  }

  /**
   * Open the modal
   */
  open() {
    this.isOpen = true;
    setTimeout(() => {
      this.xModal.classList.add('active');
    }, 150);
    document.body.classList.add('overflow-hidden'); // Prevent background scrolling
  }

  /**
   * Close the modal
   */
  close() {
    this.isOpen = false;
    this.xModal.classList.remove('active');
    document.body.classList.remove('overflow-hidden'); // Restore scrolling
    setTimeout(() => {
      this.destroy();
    }, 200);
  }

  /**
   * Destroy the modal completely
   */
  destroy() {
    // Remove event listener to prevent memory leaks
    document.removeEventListener('keydown', this.escapeListener);
    // Remove the element from DOM
    this?.xModal?.parentNode?.removeChild(this.xModal);
  }
}

class CustomSelect extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.form = this.closest('form');
    this.variantSelects = this.closest('variant-selects');
    this.options = this.querySelectorAll('option');
    this.selectedOption = this.querySelector('option[selected]') ? this.querySelector('option[selected]') : this.querySelectorAll('option')[0];
    this.init();
    this.select.style.display = 'none';
    this.select.inert = true;
    // Store bound functions
    this.boundOpenDropdown = this.openDropdown.bind(this);
    this.boundCloseDropdown = this.closeDropdown.bind(this);
    this.boundOptionClickHandler = this.optionClickHandler.bind(this);
    this.boundEscapeHandler = this.escapeHandler.bind(this);
    this.attachEventListeners();
  }

  // Initialize UI
  init() {
    let selectDiv = document.createElement('div');
    selectDiv.classList.add('custom-select');
    for (let attr of this.select.attributes) {
      if (attr.name !== 'class' && attr.name !== 'id') {
        selectDiv.setAttribute(`${attr.name}`, `${attr.value}`);
      }
    }
    selectDiv.textContent = this.selectedOption.textContent;
    this.appendChild(selectDiv);

    let optionsContainer = document.createElement('div');
    optionsContainer.classList.add('custom-options-container');
    this.options.forEach((option) => {
      let optionDiv = document.createElement('div');
      optionDiv.classList.add('custom-option');
      for (let attr of option.attributes) {
        optionDiv.setAttribute(`${attr.name}`, `${attr.value}`);
      }
      optionDiv.textContent = option.textContent;
      optionsContainer.appendChild(optionDiv);
    });
    this.appendChild(optionsContainer);

    this.customSelect = this.closest('.sort-filter-mobile .facet-filters__field')
                        ? this.closest('.sort-filter-mobile .facet-filters__field')
                        : this.closest('.variant-select') ? this.closest('.variant-select') : this.querySelector('.custom-select');
    this.customOptionsContainer = this.querySelector('.custom-options-container');
  }

  attachEventListeners() {
    this.customSelect.addEventListener('click', this.boundOpenDropdown, { once: true });
  }

  attachEventListenersAfterDropDownOpens() {
    // Delay to stop bubbling up of the event
    setTimeout(() => {
      this.customOptionsContainer.addEventListener('click', this.boundOptionClickHandler);
      window.addEventListener('click', this.boundCloseDropdown, { once: true });
      window.addEventListener('keyup', this.boundEscapeHandler, { once: true });
    }, 0);
  }

  detachEventListeners() {
    this.customOptionsContainer.removeEventListener('click', this.boundOptionClickHandler);
    window.removeEventListener('click', this.boundCloseDropdown);
    window.removeEventListener('keyup', this.boundEscapeHandler);
  }

  escapeHandler(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.boundCloseDropdown();
    }
  }

  openDropdown() {
    this.style.setProperty('--dropdown-height', `${this.querySelector('.custom-options-container').scrollHeight}px`);
    this.classList.add('active');
    this.attachEventListenersAfterDropDownOpens();
  }

  closeDropdown() {
    this.style = "null";
    this.classList = "null";
    this.detachEventListeners();
    this.attachEventListeners();
  }

  optionClickHandler(event) {
    if (event.target.classList.contains('custom-option') && event.target.getAttribute('value')) {
      this.select.value = event.target.getAttribute('value');
      this.select.querySelectorAll('option').forEach((option) => {
        option.removeAttribute('selected');
        if (option.value === event.target.getAttribute('value')) {
          option.setAttribute('selected', 'selected');
        }
      });
      if (this.form) {
        this.form.dispatchEvent(new Event('input'));
      }
      if (this.variantSelects) {
        this.variantSelects.dispatchEvent(new Event('change'));
      }
    }
  }
}

customElements.define('custom-select', CustomSelect);

//    <ul id="product-grid"
//        data-swiper-loop="true"
//        data-swiper-delay="3000"
//        data-swiper-speed="800"
//        data-swiper-pagination="true"
//        data-swiper-buttons="true"
//        data-swiper-disable-on-interaction="true">
//    </ul>
class ProductCardSwiper {
  constructor() {
    this.selectors = {
      mutationObserver: 'body',
      productGrid: '#product-grid',
      productCard: '.product-card-wrapper',
      swiperContainer: '.product_gallery_swiper',
      pagination: '.swiper-pagination',
      nextButton: '.swiper-button-next',
      prevButton: '.swiper-button-prev',
    };
    this.swiperInstances = new Map();
    this.productGrid = document.querySelector(`${this.selectors.productGrid}`);
    this.mutationObserver = document.querySelector(`${this.selectors.mutationObserver}`);
    this.init();
    this.setupMutationObserver();
    }
    isMobile() {
        return window.innerWidth < 992;
      }

    init() {
    // Initialize existing product cards
    const productCards = document.querySelectorAll(`${this.selectors.productCard}`);
    if(this.isMobile()){
  productCards.forEach((card) => {

   let swiperContainer = card.querySelector(`${this.selectors.swiperContainer}`);
    if (!swiperContainer) return;
    this.initializeSwiper(card, swiperContainer, true);

  })


    }
    else{
 productCards.forEach(card => this.setupCardEvents(card));
    }
   
  }

  setupCardEvents(card) {
    const swiperContainer = card.querySelector(`${this.selectors.swiperContainer}`);
    if (!swiperContainer) return;

    card.addEventListener('mouseenter', () => this.initializeSwiper(card, swiperContainer));
    card.addEventListener('mouseleave', () => this.destroySwiper(card));
  }

  initializeSwiper(card, swiperContainer, is_mobile = false) { 
    console.log("in it",this.productGrid);
    // Skip if swiper already initialized for this card
    if (this.swiperInstances.has(card)) return;
    let slides = swiperContainer.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) return;

    const options = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: this.getBooleanAttribute(this.productGrid, 'data-swiper-loop', false),
      speed: this.getNumberAttribute(this.productGrid, 'data-swiper-speed', 500)
    };

    if(!is_mobile){
      options.autoplay = {
        delay: this.getNumberAttribute(this.productGrid, 'data-swiper-delay', 2000),
        disableOnInteraction: this.getBooleanAttribute(this.productGrid, 'data-swiper-disable-on-interaction', true)
      };

    }

    if(this.getBooleanAttribute(this.productGrid, 'data-swiper-buttons', true) && !is_mobile) {
      options.navigation = {
        nextEl: card.querySelector(this.selectors.nextButton),
        prevEl: card.querySelector(this.selectors.prevButton)
      };
    }
    else if(this.getBooleanAttribute(this.productGrid, 'data-swiper-buttons', true) && is_mobile){
      options.navigation = {
        nextEl: card.querySelector(this.selectors.nextButton),
        prevEl: card.querySelector(this.selectors.prevButton),
        enabled: false,
      };
    }

    if(this.getBooleanAttribute(this.productGrid, 'data-swiper-pagination', false)) {
      console.log("test5",this.selectors.pagination)
      console.log("card",card)
      console.log("have page",card.querySelector(this.selectors.pagination));
      options.pagination = {
        el: card.querySelector(this.selectors.pagination),
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 3
      }
      
    }
    

    // Initialize Swiper
    const swiper = new Swiper(swiperContainer, options);

    // Store swiper instance
    this.swiperInstances.set(card, swiper);
  }

  destroySwiper(card) {
  const swiper = this.swiperInstances.get(card);

  if (swiper) {
    // destroy swiper
    swiper.destroy(true, true);

    // manually clear pagination
    const paginationEl = card.querySelector('.swiper-pagination');
    if (paginationEl) {
      paginationEl.innerHTML = '';
      paginationEl.classList.remove('swiper-pagination-clickable');
    }

    this.swiperInstances.delete(card);
  }
}

  setupMutationObserver() {
    if (!this.mutationObserver) return;
    // Create mutation observer for dynamically loaded product cards
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            // Check if added node is a product card or contains product cards
            if (node.classList && node.classList.contains(`${this.selectors.productCard}`)) {
              this.setupCardEvents(node);
            } else if (node.querySelectorAll) {
              const cards = node.querySelectorAll(`${this.selectors.productCard}`);
              cards.forEach(card => this.setupCardEvents(card));
            }
          });
        }
      });
    });
    // Start observing the product grid for changes
    observer.observe(this.mutationObserver, {
      childList: true,
      subtree: true
    });
  }

  // Helper methods to get typed attribute values with defaults
  getBooleanAttribute(element, attributeName, defaultValue) {
    if (!element) return defaultValue;
    const value = element.getAttribute(attributeName);
    return value === null ? defaultValue : value === 'true';
  }

  getNumberAttribute(element, attributeName, defaultValue) {
    if (!element) return defaultValue;
    const value = element.getAttribute(attributeName);
    return value === null ? defaultValue : parseInt(value, 10);
  }

  getStringAttribute(element, attributeName, defaultValue) {
    if (!element) return defaultValue;
    const value = element.getAttribute(attributeName);
    return value === null ? defaultValue : value;
  }
}

// Initialize the product card swiper functionality when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.productCardSwipers = new ProductCardSwiper();
});

class ProductCustomizationImageUpload {
  constructor(config = {}) {
    this.selectors = {
      container: config.container || '.customization-fields__image-container',
      fileInput: config.fileInput || '#fileInput',
      fileInputLabel: config.fileInputLabel || '.customization-fields__image-label',
      note: config.note || '.customization-fields__image-note',
      errorClass: config.errorClass || '.customization-fields__image-error',
      previewContainerClass: config.previewContainerClass || 'customization-fields__image-preview-container',
      previewImageClass: config.previewImageClass || 'customization-fields__image-preview',
      previewImageUploading: config.previewImageUploading || 'customization-fields__image-preview-uploading',
      previewWrapperClass: config.previewWrapperClass || 'customization-fields__image-preview-wrapper',
      removeButtonClass: config.removeButtonClass || 'customization-fields__image-preview-remove',
      productInfo: config.productInfo || 'product-info',
    };
    this.selectedFiles = [];
    this.container = document.querySelector(this.selectors.container);
    if (!this.container) return;
    this.productInfo = this.container.closest(this.selectors.productInfo);
    this.fileInput = this.container.querySelector(this.selectors.fileInput);
    this.errorContainer = this.container.querySelector(this.selectors.errorClass);
    this.erroMessages = {
      maxSize: this.container.dataset.errorMessageMaxSize,
      fileType: this.container.dataset.errorMessageFileType,
      maxCount: this.container.dataset.errorMessageMaxCount,
      uploadError: this.container.dataset.errorMessageUploadError,
    };
    this.previewContainer = this.createPreviewContainer();
    this.isUploading = false;
    this.imageLimit = parseInt(this.fileInput.dataset.imageCount);
    this.uploadedUrls = [];
    this.fileToUrlMap = new Map(); // Add this to track which file corresponds to which URL
    this.init();
  }

  createPreviewContainer() {
    const container = document.createElement('div');
    container.className = this.selectors.previewContainerClass;
    this.fileInput.parentNode.appendChild(container);
    return container;
  }

  init() {
    this.fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
  }

  validateFiles(files) {
    const maxSize = 1 * 1024 * 1024; // 1 MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/hevc', 'image/heif'];

    // Find those files that are larger than 1 MB or have an invalid file type
    const invalidFile = Array.from(files).find((file) => file.size > maxSize || !allowedTypes.includes(file.type));

    if (invalidFile) {
      if (invalidFile.size > maxSize) {
        this.setError(this.erroMessages.maxSize);
      } else {
        this.setError(this.erroMessages.fileType);
      }
      return false;
    }

    return true;
  }

  setError(message) {
    this.errorContainer.textContent = message;
  }

  resetError() {
    this.errorContainer.textContent = '';
  }

  handleFileSelect(e) {
    const files = Array.from(e.target.files);

    if (this.selectedFiles.length + files.length > this.imageLimit) {
      this.setError(this.erroMessages.maxCount);
      return;
    }

    const allFiles = [...this.selectedFiles, ...files];

    if (!this.validateFiles(allFiles)) return;

    this.resetError();
    this.selectedFiles = allFiles;
    this.handleFileUpload();
  }

  updatePreview(uploading = true) {
    this.previewContainer.innerHTML = '';
    this.selectedFiles.forEach((file, index) => {
      const preview = document.createElement('div');
      preview.className = this.selectors.previewWrapperClass;
      if (uploading) {
        preview.classList.add(this.selectors.previewImageUploading);
        preview.style.setProperty('--animation-delay', index);
      } else {
        preview.classList.remove(this.selectors.previewImageUploading);
        preview.style.setProperty('--animation-delay', null);
      }

      const img = document.createElement('img');
      img.src = URL.createObjectURL(file);
      img.className = this.selectors.previewImageClass;

      const removeBtn = document.createElement('button');
      removeBtn.innerHTML = '×';
      removeBtn.className = this.selectors.removeButtonClass;
      removeBtn.onclick = () => this.handleRemoveFile(index);

      preview.appendChild(img);
      preview.appendChild(removeBtn);
      this.previewContainer.appendChild(preview);
    });
    this.updateFormInput();
  }

  updateFormInput() {
    if (this.selectedFiles.length === this.imageLimit) {
      this.container.querySelector(this.selectors.fileInputLabel).classList.add('hidden');
      this.container.querySelector(this.selectors.note).classList.add('hidden');
    } else {
      this.container.querySelector(this.selectors.fileInputLabel).classList.remove('hidden');
      this.container.querySelector(this.selectors.note).classList.remove('hidden');
    }
  }

  handleRemoveFile(index) {
    const removedFile = this.selectedFiles[index];
    // Remove the URL associated with this file from uploadedUrls
    if (this.fileToUrlMap.has(removedFile)) {
      const urlIndex = this.uploadedUrls.indexOf(this.fileToUrlMap.get(removedFile));
      if (urlIndex > -1) {
        this.uploadedUrls.splice(urlIndex, 1);
      }
      this.fileToUrlMap.delete(removedFile);
    }

    this.selectedFiles.splice(index, 1);
    this.updatePreview(this.isUploading);
    this.fileInput.value = '';

    // Clear the data-image-urls attribute when all files are removed
    if (this.selectedFiles.length === 0 && this.productInfo) {
      this.productInfo.dataset.imageUrls = "";
      this.uploadedUrls = []; // Clear the uploadedUrls array
    }

    if (this.selectedFiles.length === 0) {
      this.resetError();
    }
  }

  async handleFileUpload() {
    if (this.isUploading || this.selectedFiles.length === 0) return;

    this.isUploading = true;
    const formData = new FormData();

    // Only upload files that haven't been uploaded yet
    const newFiles = this.selectedFiles.filter(file => !this.fileToUrlMap.has(file));
    newFiles.forEach((file) => {
      formData.append('image', file);
    });

    // If no new files to upload, skip the request
    if (newFiles.length === 0) {
      this.isUploading = false;
      this.updatePreview(false);
      return;
    }

    try {
      this.updatePreview(this.isUploading);
      const response = await fetch('https://oms.fnp.sa/backend/api/images/upload', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      const data = await response.json();
      // Handle successful upload
      this.handleUploadSuccess(data, newFiles);
    } catch (error) {
      this.handleUploadError(error);
    } finally {
      this.isUploading = false;
      this.updatePreview(this.isUploading);
    }
  }

  handleUploadSuccess(data, uploadedFiles) {
    if (data.imageUrls) {
      // Map new files to their URLs
      uploadedFiles.forEach((file, index) => {
        this.fileToUrlMap.set(file, data.imageUrls[index]);
      });

      // Rebuild the uploadedUrls array based on current selectedFiles order
      this.uploadedUrls = this.selectedFiles.map(file => this.fileToUrlMap.get(file)).filter(url => url !== undefined);

      if(this.productInfo) {
        this.productInfo.dataset.imageUrls = JSON.stringify(this.uploadedUrls);
      }
    }
  }

  handleUploadError(error) {
    console.error(`🚀 || product-customization-fields.js:2087 || ProductCustomizationFields || handleUploadError || error:`,error);
    if(this.productInfo) {
      this.productInfo.dataset.imageUrls = "";
    }
    this.setError(this.erroMessages.uploadError);
  }

  getImageUrls() {
    // Return the array of image URLs
    return this.uploadedUrls;
  }

  validateImages() {
    // Validate that images are uploaded when image upload is required
    if (this.imageLimit > 0) {
      return this.uploadedUrls.length > 0;
    }
    return true;
  }
}
class ProductCustomizationMessage {
  constructor(config = {}) {
    this.selectors = {
      container: config.container || '.customization-fields__message-container',
      messageInput: config.messageInput || '.customization-fields__message-input',
      errorContainer: config.errorClass || '.customization-fields__message-error',
      productInfo: config.productInfo || 'product-info',
      productForm: config.productForm || 'product-form'
    };

    this.container = document.querySelector(this.selectors.container);
    if (!this.container) return;

    this.messageInput = this.container.querySelector(this.selectors.messageInput);
    this.errorContainer = this.container.querySelector(this.selectors.errorContainer);
    this.productInfo = this.container.closest(this.selectors.productInfo);
    this.productForm = this.productInfo?.querySelector(this.selectors.productForm);
    this.init();
  }

  init() {
    if (this.messageInput) {
      this.messageInput.addEventListener('input', this.handleMessageChange.bind(this));
    }
  }

  handleMessageChange(event) {
    const message = event.target.value.trim();

    if (this.productForm) {
      this.productForm.dataset.message = message;
    }
  }
}

// Initialize the class when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.messageInput = new ProductCustomizationMessage({
    container: '.customization-fields__message-container',
  });
});

// Initialize the class when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.imageFromInput = new ProductCustomizationImageUpload({
    container: '.customization-fields__image-container',
    fileInput: '#fileInput',
    errorClass: '.customization-fields__image-error',
    previewContainerClass: 'customization-fields__image-preview-container',
    previewImageClass: 'customization-fields__image-preview',
    previewImageUploading: 'customization-fields__image-preview-uploading',
    previewWrapperClass: 'customization-fields__image-preview-wrapper',
    removeButtonClass: 'customization-fields__image-preview-remove',
  });
});

class CollectionSlider extends HTMLElement {
  constructor() {
    super();
    this.dataInJson = this.querySelector('data-in-json');
    this.tabButtons = this.querySelectorAll('.collection-slider__tab');
    this.swiperNext = this.querySelector('.collection-slider-next');
    this.swiperPrev = this.querySelector('.collection-slider-prev');
    // City selector removed - using header location dropdown instead

    this.swiperObject = undefined;
    this.isCityBasedFilteringEnabled = this.getBooleanAttribute(this, 'data-is-city-based-filtering-enabled', true);
    this.defaultCity = this.getStringAttribute(this, 'data-default-city', 'city_doha');
    this.currentCity = null;
    this.cityProductArrays = {};

    this.convertData();
    this.init();
    // this.setupIntersectionObserver(); // Autoplay disabled
  }

  init() {
    this.attachEventListeners();
    window.addEventListener('DOMContentLoaded', this.initializeSwiperOnload.bind(this), { once: true });

    if (this.isCityBasedFilteringEnabled) {
      document.addEventListener('city-changed', this.handleCityChange.bind(this));
      this.initializeCityBasedFiltering();
      
      // Listen for changes from the header location dropdown
      this.setupLocationDropdownListener();
    }
  }

  /**
   * Setup listener for header location dropdown changes
   */
  setupLocationDropdownListener() {
    // Watch for changes in localStorage city values
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = (key, value) => {
      originalSetItem.call(localStorage, key, value);
      
      // If city changed, update collection slider
      if ((key === 'city' || key === 'city_value') && value !== this.currentCity) {
        this.currentCity = value;
        this.renderSwiperForCity(value);
      }
    };
    
    // Also listen for direct city change events from header
    document.addEventListener('city-changed', (event) => {
      const newCity = event.detail.value || event.detail.textValue;
      if (newCity !== this.currentCity) {
        this.currentCity = newCity;
        this.renderSwiperForCity(newCity);
      }
    });
  }

  attachEventListeners() {
    // Only attach event listeners if there are multiple tabs
    if (this.tabButtons.length > 1) {
      this.tabButtons.forEach(button => {
        button.addEventListener('click', this.handleTabClick.bind(this));
      });
    }

    // City selector removed - using header location dropdown instead
  }

  handleCityChange(event) {
    const selectedCity = event.detail.value || event.detail.textValue;
    this.currentCity = selectedCity;
    
    // Re-render swiper with city-filtered products using priority logic
    this.renderSwiperForCity(selectedCity);
  }

  initializeSwiperOnload(event, cityTag = localStorage.getItem('city') || this.defaultCity) {
    // Check if we have valid data
    if (!this.dataInJson || Object.keys(this.dataInJson).length === 0) {
      console.warn('CollectionSlider: No data available for initialization');
      return;
    }

    // Create city product arrays if city-based filtering is enabled
    if (this.isCityBasedFilteringEnabled) {
      this.createCityProductArrays();
      this.updateCitySelector();
    }

    let activeTab = [...this.tabButtons].find(domNode => domNode.classList.contains('active'));
    
    // If no active tab found (single collection case), use the first available data
    if (!activeTab && this.tabButtons.length === 0) {
      // For single collection, get the first (and only) data entry
      const firstDataKey = Object.keys(this.dataInJson)[0];
      if (firstDataKey && this.dataInJson[firstDataKey]) {
        this.updateSubHeading(this.dataInJson[firstDataKey]);
        this.updateViewAllLink(this.dataInJson[firstDataKey]);
        
        let swiperData;
        if (this.isCityBasedFilteringEnabled && this.currentCity && this.currentCity !== '') {
          // Use priority logic: selected city first, then others
          swiperData = this.createPriorityProductArray(firstDataKey, this.currentCity);
        } else {
          // Use all products if no city selected or city filtering disabled
          swiperData = this.dataInJson[firstDataKey].products || [];
        }
        
        this.initializeSwiper(swiperData);
      }
      return;
    }
    
    // Original logic for multiple collections
    if (!activeTab) return;
    
    const tabData = this.dataInJson[`${activeTab.dataset.id}`];
    if (!tabData) {
      console.warn('CollectionSlider: No data found for active tab');
      return;
    }
    
    this.updateSubHeading(tabData);
    this.updateViewAllLink(tabData);

    let swiperData;
    if (this.isCityBasedFilteringEnabled && this.currentCity && this.currentCity !== '') {
      // Use priority logic: selected city first, then others
      swiperData = this.createPriorityProductArray(activeTab.dataset.id, this.currentCity);
    } else {
      // Use all products if no city selected or city filtering disabled
      swiperData = tabData.products || [];
    }

    this.initializeSwiper(swiperData);
  }

  convertData() {
    if (!this.dataInJson) return;
    try {
      let jsonData = JSON.parse(decodeBase64HTML(this.dataInJson.innerHTML));
      this.dataInJson.remove();
      this.dataInJson = jsonData;
    } catch (error) {
      console.error('CollectionSlider: Error parsing data:', error);
      this.dataInJson = {};
    }
  }

  handleTabClick(event) {
    event.preventDefault();
    const tabId = event.currentTarget.dataset.id;
    if (typeof this.dataInJson[`${tabId}`] !== 'object' || Object.keys(this.dataInJson[`${tabId}`]).length === 0) return;
    this.tabButtons.forEach(button => {
      button.classList.remove('active');
    });
    event.currentTarget.classList.add('active');
    this.updateSubHeading(this.dataInJson[`${tabId}`]);
    this.updateViewAllLink(this.dataInJson[`${tabId}`]);
    
    let swiperData;
    if (this.isCityBasedFilteringEnabled && this.currentCity && this.currentCity !== '') {
      // Use priority logic: selected city first, then others
      swiperData = this.createPriorityProductArray(tabId, this.currentCity);
    } else {
      // Use all products if no city selected or city filtering disabled
      swiperData = this.dataInJson[`${tabId}`].products || [];
    }
    
    this.initializeSwiper(swiperData);
  }

  updateSubHeading(dataObject) {
    let subHeading = this.querySelector('.collection-slider__sub-heading');
    if (!subHeading) return;
    subHeading.textContent = dataObject.subHeading;
  }

  updateViewAllLink(dataObject) {
    let viewAllLink = this.querySelector('.collection-slider-view-all-button');
    if (!viewAllLink) return;
    viewAllLink.href = dataObject.collection_url;
    viewAllLink.textContent = dataObject.viewAllLabel;
  }

  initializeSwiper(products) {
    // Check if products array exists and has items
    if (!products || products.length === 0) {
      console.warn('CollectionSlider: No products to display');
      return;
    }

    let slidesOnDesktop = this.getNumberAttribute(this, 'data-slides-on-desktop', 4);
    let slidesOnTablet = this.getNumberAttribute(this, 'data-slides-on-tablet', 3);
    let slidesOnMobile = this.getNumberAttribute(this, 'data-slides-on-mobile', 2);
    let isLoopEnabled = this.getBooleanAttribute(this, 'data-enable-loop', false);
    // let isAutoPlayEnabled = this.getBooleanAttribute(this, 'data-enable-autoplay', false); // Autoplay disabled
    // let autoPlaySpeed = this.getNumberAttribute(this, 'data-autoplay-speed', 5000); // Autoplay disabled
    // let disableOnInteraction = this.getBooleanAttribute(this, 'data-disable-on-interaction', true); // Autoplay disabled

    let slides = products.map((product, index) => {
      return `<div class="product-card-animation" style="animation-delay: ${index / 10}s">${decodeBase64HTML(product.card)}</div>`;
    });

    // Adjust initial slide based on available slides
    let initialSlide = Math.min(2, Math.max(0, slides.length - 1));

    let swiperOptions = {
      navigation: {
        nextEl: this.swiperNext,
        prevEl: this.swiperPrev,
      },
      initialSlide: initialSlide,
      loop: isLoopEnabled && slides.length > 1, // Only enable loop if there are multiple slides
      slidesPerView: slidesOnMobile,
      spaceBetween: 12,
      breakpoints: {
        600: {
          slidesPerView: slidesOnMobile,
          spaceBetween: 12
        },
        950: {
          slidesPerView: slidesOnTablet,
          spaceBetween: 25
        },
        1250: {
          slidesPerView: slidesOnDesktop,
          spaceBetween: 27
        }
      }
    }

    // Autoplay disabled
    // if (!isAutoPlayEnabled) {
    //   swiperOptions.autoplay = false;
    // } else {
    //   swiperOptions.autoplay = {
    //     delay: autoPlaySpeed * 1000,
    //     disableOnInteraction: disableOnInteraction
    //   };
    // }

    swiperOptions.virtual = {
      slides: slides
    };

    // Check if swiper container exists
    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) {
      console.warn('CollectionSlider: Swiper container not found');
      return;
    }

    if (this.swiperObject) {
      this.swiperObject = this.destroySwiper(this.swiperObject);
    } else {
      const swiperWrapper = this.querySelector('.swiper-wrapper');
      if (swiperWrapper) {
        swiperWrapper.innerHTML = "";
      }
    }
    this.swiperObject = new Swiper(swiperContainer, swiperOptions);

    this.swiperObject.on('slideChange', function () {
      if (typeof initialCurrencyChange === 'function') initialCurrencyChange();
    });

  }

  destroySwiper(swiper) {
    swiper.wrapperEl.innerHTML = '';
    swiper.destroy();
    swiper = undefined;
    return swiper;
  }

  // setupIntersectionObserver() { // Autoplay disabled
  //   const options = {
  //     root: null,
  //     rootMargin: '20px',
  //     threshold: 0
  //   };

  //   const observer = new IntersectionObserver((entries) => {
  //     entries.forEach(entry => {
  //       if (entry.isIntersecting && this.swiperObject) {
  //         this.swiperObject.autoplay.start();
  //       } else if (this.swiperObject) {
  //         this.swiperObject.autoplay.stop();
  //       }
  //     });
  //   }, options);

  //   observer.observe(this);
  // }

  /**
   * Initialize city-based filtering
   */
  initializeCityBasedFiltering() {
    // Get current city from localStorage, clean up format
    let storedCity = localStorage.getItem('city_value') || localStorage.getItem('city') || this.defaultCity;
    if (storedCity.startsWith('city_')) {
      storedCity = storedCity.replace('city_', '').replace(/[-_]/g, ' ');
      storedCity = storedCity.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    
    this.currentCity = storedCity;
    
    this.createCityProductArrays();
    this.updateCitySelector();
  }

  /**
   * Handle city selection from header location dropdown
   * This method is now primarily for compatibility and debugging
   */
  handleCitySelection(selectedCity) {
    this.currentCity = selectedCity;
    
    // Re-render swiper with city-filtered products using priority logic
    this.renderSwiperForCity(selectedCity);
  }

  /**
   * Create city-based product arrays with priority logic
   */
  createCityProductArrays() {
    if (!this.dataInJson || Object.keys(this.dataInJson).length === 0) return;

    const cities = ['Riyadh', 'Al Khobar', 'Dammam', 'Dhahran', 'Jubail', 'Jeddah','Qatif','Madinah','Meccah'];
    this.cityProductArrays = {};

    // Process each collection block
    Object.keys(this.dataInJson).forEach(blockId => {
      const blockData = this.dataInJson[blockId];
      
      // Use pre-generated city arrays if available, otherwise create them
      if (blockData.cityProductArrays) {
        this.cityProductArrays[blockId] = blockData.cityProductArrays;
      } else {
        // Fallback to the old method if cityProductArrays not available
        this.cityProductArrays[blockId] = {};
        cities.forEach(city => {
          this.cityProductArrays[blockId][city] = [];
        });

        if (!blockData.products) return;

        // Filter products by city - only show products available in that city
        cities.forEach(city => {
          const cityProducts = blockData.products.filter(product => {
            let listOfCities = product.list_of_cities;
            
            // Handle different metafield formats
            if (typeof listOfCities === 'string') {
              try {
                listOfCities = JSON.parse(listOfCities);
              } catch (e) {
                // If it's a comma-separated string
                listOfCities = listOfCities.split(',').map(c => c.trim());
              }
            }
            
            // Show products that either:
            // 1. Have no city restrictions (available everywhere)
            // 2. Explicitly include the selected city
            if (!listOfCities || !Array.isArray(listOfCities) || listOfCities.length === 0) {
              return true; // Available everywhere
            }
            
            // Check if city is in the list (case-insensitive)
            return listOfCities.some(c => c.toLowerCase() === city.toLowerCase());
          });

          this.cityProductArrays[blockId][city] = cityProducts;
        });
      }
    });

  }

  /**
   * Update city selector to reflect current city (now unused but kept for compatibility)
   */
  updateCitySelector() {
    // This method is now primarily for debugging since we use header dropdown
  }

  /**
   * Render swiper for specific city with priority logic
   */
  renderSwiperForCity(selectedCity) {
    let activeTab = [...this.tabButtons].find(domNode => domNode.classList.contains('active'));
    
    // Get the appropriate data
    let blockId;
    if (!activeTab && this.tabButtons.length === 0) {
      // Single collection case
      blockId = Object.keys(this.dataInJson)[0];
    } else if (activeTab) {
      blockId = activeTab.dataset.id;
    }

    if (!blockId || !this.dataInJson[blockId]) return;

    let swiperData;
    if (selectedCity && selectedCity !== '' && this.cityProductArrays[blockId]) {
      // Priority logic: Show selected city products first, then others
      swiperData = this.createPriorityProductArray(blockId, selectedCity);
    } else {
      // Use all products if no city selected (All Cities option)
      swiperData = this.dataInJson[blockId].products || [];
    }

    this.initializeSwiper(swiperData);
  }

  /**
   * Create priority product array: selected city first, then others
   */
  createPriorityProductArray(blockId, selectedCity) {
    if (!this.cityProductArrays[blockId] || !this.dataInJson[blockId]) return [];

    const allProducts = this.dataInJson[blockId].products || [];
    const selectedCityProducts = this.cityProductArrays[blockId][selectedCity] || [];
    
    // Get products that are NOT in the selected city
    const otherProducts = allProducts.filter(product => {
      let listOfCities = product.list_of_cities;
      
      // Handle different metafield formats
      if (typeof listOfCities === 'string') {
        try {
          listOfCities = JSON.parse(listOfCities);
        } catch (e) {
          // If it's a comma-separated string
          listOfCities = listOfCities.split(',').map(c => c.trim());
        }
      }
      
      // Include products that:
      // 1. Have no city restrictions (available everywhere) BUT not already in selected city array
      // 2. Don't include the selected city
      if (!listOfCities || !Array.isArray(listOfCities) || listOfCities.length === 0) {
        // Check if this product is already in selected city products
        return !selectedCityProducts.some(cityProduct => cityProduct.id === product.id);
      }
      
      // Don't include if city is in the list (these are already in selectedCityProducts)
      return !listOfCities.some(c => c.toLowerCase() === selectedCity.toLowerCase());
    });

    // Combine: selected city products first, then others
    const priorityArray = [...selectedCityProducts, ...otherProducts];
    
    
    return priorityArray;
  }

  /**
   * Debug method to check city filtering
   */
  debugCityFiltering() {
    
    // Check if metafield data is properly loaded
    Object.keys(this.dataInJson).forEach(blockId => {
      const blockData = this.dataInJson[blockId];
      if (blockData.products) {
        blockData.products.slice(0, 3).forEach(product => {
        });
      }
    });
  }

  // Helper methods to get typed attribute values with defaults
  getBooleanAttribute(element, attributeName, defaultValue) {
    if (!element) return defaultValue;
    const value = element.getAttribute(attributeName);
    return value === null ? defaultValue : value === 'true';
  }

  getNumberAttribute(element, attributeName, defaultValue) {
    if (!element) return defaultValue;
    const value = element.getAttribute(attributeName);
    return value === null ? defaultValue : parseFloat(parseFloat(value).toFixed(2));
  }

  getStringAttribute(element, attributeName, defaultValue) {
    if (!element) return defaultValue;
    const value = element.getAttribute(attributeName);
    return value === null ? defaultValue : value;
  }
}

customElements.define('collection-slider', CollectionSlider);

// Global debug function for collection slider
window.debugCollectionSlider = function() {
  const collectionSliders = document.querySelectorAll('collection-slider');
  collectionSliders.forEach(slider => {
    if (slider.debugCityFiltering) {
      slider.debugCityFiltering();
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  let giftSections = document.querySelectorAll('.main-section-container-gift');
  if(!Array.isArray(giftSections) && giftSections.length <= 0) return;

  const options = {
    root: null,
    rootMargin: '20px',
    threshold: 0
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        let slideImages = [...entry.target.querySelectorAll('.swiper-slide img')].map(img => `${img.src}`);
        let data = new ImageCache(slideImages, false);
      }
    });
  }, options);

  giftSections.forEach(section => {
    observer.observe(section);
  })
});

document.addEventListener('DOMContentLoaded', function() {
  function updateMegaMenuState() {
    const anyOpen = Array.from(document.querySelectorAll('.mega-menu')).some(d => d.open);
    document.body.classList.toggle('megamenu-open', anyOpen);
  }

  document.querySelectorAll('.mega-menu').forEach(details => {
    details.addEventListener('toggle', updateMegaMenuState);
  });

  updateMegaMenuState();
});

document.addEventListener('DOMContentLoaded', function() {
  // Filter open/close logic
  var filterDisclosure = document.querySelector('.mobile-facets__disclosure');
  if (filterDisclosure) {
    filterDisclosure.addEventListener('toggle', function() {
      document.body.classList.toggle('filter-open', filterDisclosure.open);
    });
    // Initial state
    document.body.classList.toggle('filter-open', filterDisclosure.open);
  }
});

/*
  ┌─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
  │ City Filter Collection URL Interception                                                                        │
  └─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┘
*/

// Function to add city filter to collection URLs
function addCityFilterToURL(url, city) {
  if (!city || !url.includes('/collections/')) return url;
  
  try {
    const urlObj = new URL(url, window.location.origin);
    const params = new URLSearchParams(urlObj.search);
    
    // Only add if not already present
    if (!params.has('filter.p.m.custom.list_of_cities')) {
      // Special handling for Al Khobar on collection pages - use hyphenated version
      const cityForFilter = (city.toLowerCase() === 'al khobar' && urlObj.pathname.includes('/collections/')) 
        ? 'Al-Khobar' 
        : city;
      
      params.set('filter.p.m.custom.list_of_cities', cityForFilter);
      return `${urlObj.pathname}?${params.toString()}`;
    }
    
    return url;
  } catch (error) {
    console.warn('Error adding city filter to URL:', error);
    return url;
  }
}

// Global collection link interception
(function() {
  // Intercept all collection links immediately
  function interceptCollectionLinks() {
    const currentCity = localStorage.getItem('city_value') || localStorage.getItem('city');
    if (!currentCity) return;

    // Use event delegation for better performance
    document.addEventListener('click', function(event) {
      const link = event.target.closest('a[href*="/collections/"]');
      if (link && link.href && !link.dataset.cityIntercepted) {
        const originalHref = link.href;
        const newHref = addCityFilterToURL(originalHref, currentCity);
        
        if (newHref !== originalHref) {
          event.preventDefault();
          event.stopPropagation();
          
          // Mark as intercepted to prevent double interception
          link.dataset.cityIntercepted = 'true';
          
          // Navigate to the new URL
          window.location.href = newHref;
        }
      }
    }, true); // Use capture phase for early interception
  }

  // Start interception immediately
  interceptCollectionLinks();
  
  // Also intercept when DOM is ready for any dynamically added links
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', interceptCollectionLinks);
  }
})();

// Also make the function globally available
window.addCityFilterToURL = addCityFilterToURL;

/*
  ┌─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
  │ Global Cart Management Functions                                                                                │
  └─────────────────────────────────────────────────────────────────────────────────────────────────────────────────┘
*/

/**
 * Global function to clear cart from anywhere in the application
 * This is useful for language/city changes that require cart clearing
 */
window.clearCartGlobally = async function() {
  try {
    
    // Find cart items element
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    
    if (cartItems && typeof cartItems.clearCart === 'function') {
      await cartItems.clearCart();
      return true;
    } else {
      console.warn('Cart items element or clearCart method not found');
      return false;
    }
  } catch (error) {
    console.error('Failed to clear cart globally:', error);
    return false;
  }
};

/**
 * Global city matching utilities for consistent city comparison across the site
 */
window.CityMatcher = {
  /**
   * Normalize city name for comparison - handles case sensitivity and hyphens
   */
  normalize(cityName) {
    if (!cityName) return '';
    
    let normalized = cityName.toLowerCase().trim();
    
    // Remove common prefixes/suffixes
    normalized = normalized.replace(/^(city of|deliver to|يسلم إلى:?)\s*/i, '');
    normalized = normalized.replace(/\s+(city|مدينة)$/i, '');
    
    // Normalize hyphens, underscores, and spaces for consistent matching
    normalized = normalized.replace(/[-\s_]+/g, ' ').trim();
    
    // Handle common abbreviations
    normalized = normalized.replace(/\balk\b/g, 'al ');
    normalized = normalized.replace(/\bkh\b/g, 'khobar');
    
    return normalized;
  },

  /**
   * Get canonical city name for a given city
   */
  getCanonicalName(cityName) {
    const normalized = this.normalize(cityName);
    
    // City mappings to canonical names
    const cityMappings = {
      'riyadh': 'Riyadh',
      'الرياض': 'Riyadh',
      'al khobar': 'Al Khobar',
      'al-khobar': 'Al Khobar',
      'al_khobar': 'Al Khobar',
      'alkhobar': 'Al Khobar',
      'khobar': 'Al Khobar',
      'الخبر': 'Al Khobar',
      'dammam': 'Dammam',
      'الدمام': 'Dammam',
      'dhahran': 'Dhahran',
      'الظهران': 'Dhahran',
      'jubail': 'Jubail',
      'الجبيل': 'Jubail',
      'jeddah': 'Jeddah',
      'جدة': 'Jeddah',
      'القطيف': 'Qatif',
      'المدينة':'Madinah',
      'مكه':'Meccah'
    };
    
    return cityMappings[normalized] || cityName;
  },

  /**
   * Check if two city names refer to the same city
   */
  match(city1, city2) {
    if (!city1 || !city2) return false;
    
    const normalized1 = this.normalize(city1);
    const normalized2 = this.normalize(city2);
    
    // Direct match after normalization
    if (normalized1 === normalized2) {
      return true;
    }
    
    // Check if they map to the same canonical name
    const canonical1 = this.getCanonicalName(city1);
    const canonical2 = this.getCanonicalName(city2);
    
    return canonical1 === canonical2;
  },

  /**
   * Check if a city is available in a list of cities (case and hyphen insensitive)
   */
  isAvailableInList(targetCity, cityList) {
    if (!targetCity || !cityList) return false;
    
    // Handle different input formats
    let cities = [];
    if (typeof cityList === 'string') {
      cities = cityList.split(',').map(c => c.trim());
    } else if (Array.isArray(cityList)) {
      cities = cityList;
    } else {
      return false;
    }
    
    // If no cities specified, allow all
    if (cities.length === 0) return true;
    
    // Check if target city matches any city in the list
    return cities.some(city => this.match(targetCity, city));
  },

  /**
   * Test function to verify city matching works correctly
   */
  test() {
    
    // Test case variations for Al Khobar - including the new variations requested
    const testCases = [
      ['Al Khobar', 'Al-Khobar', true],
      ['Al Khobar', 'AL-KHOBAR', true],
      ['Al Khobar', 'al-khobar', true],
      ['Al Khobar', 'Al-khobar', true],
      ['Al Khobar', 'alkhobar', true],
      ['Al Khobar', 'Khobar', true],
      ['Al Khobar', 'الخبر', true],
      ['Al-Khobar', 'Al Khobar', true],
      ['AL-KHOBAR', 'al khobar', true],
      ['Al-khobar', 'Al Khobar', true],
      ['al_khobar', 'Al Khobar', true],
      ['AL-KHOBAR', 'Al-khobar', true],
      // Test the specific variations mentioned in the request
      ['AL-KHOBAR', 'Al-khobar', true],
      ['Al-khobar', 'AL-KHOBAR', true],
      ['Al Khobar', 'Riyadh', false],
      ['Al Khobar', 'Dammam', false]
    ];
    
    let passed = 0;
    let failed = 0;
    
    testCases.forEach(([city1, city2, expected], index) => {
      const result = this.match(city1, city2);
      const status = result === expected ? '✅ PASS' : '❌ FAIL';
      
      if (result === expected) {
        passed++;
      } else {
        failed++;
      }
    });
    
    
    return { passed, failed, total: testCases.length };
  },

  /**
   * Test function specifically for Al Khobar variations
   */
  testAlKhobarVariations() {
    
    const alKhobarVariations = [
      'Al Khobar',
      'AL-KHOBAR', 
      'Al-khobar',
      'al-khobar',
      'Al-Khobar',
      'al_khobar',
      'alkhobar',
      'Khobar',
      'الخبر'
    ];
    
    let allPassed = true;
    
    for (let i = 0; i < alKhobarVariations.length; i++) {
      for (let j = i + 1; j < alKhobarVariations.length; j++) {
        const variation1 = alKhobarVariations[i];
        const variation2 = alKhobarVariations[j];
        const result = this.match(variation1, variation2);
        const status = result ? '✅' : '❌';
        if (!result) allPassed = false;
      }
    }
    
    
    return allPassed;
  },

  /**
   * Test function for city display name translation
   */
  testCityDisplayNames() {
    
    const testCities = ['Jubail', 'Al Khobar', 'Riyadh', 'Dammam', 'Dhahran', 'Jeddah','Qatif','Madinah','Meccah'];
    const languages = [false, true]; // false = English, true = Arabic
    
    languages.forEach(isArabic => {
      
      testCities.forEach(city => {
        const displayName = this.getCityDisplayName(city, isArabic);
      });
    });
    
    return true;
  },

  /**
   * Get the display name for a city based on language
   */
  getCityDisplayName(cityName, isArabic) {
    if (!cityName) return '';
    
    // City name mapping for Arabic translation
    const cityMappings = {
      'Riyadh': isArabic ? 'الرياض' : 'Riyadh',
      'Al Khobar': isArabic ? 'الخبر' : 'Al Khobar',
      'Al-Khobar': isArabic ? 'الخبر' : 'Al-Khobar',
      'AL-KHOBAR': isArabic ? 'الخبر' : 'AL-KHOBAR',
      'Al-khobar': isArabic ? 'الخبر' : 'Al-khobar',
      'al-khobar': isArabic ? 'الخبر' : 'al-khobar',
      'Dammam': isArabic ? 'الدمام' : 'Dammam',
      'Dhahran': isArabic ? 'الظهران' : 'Dhahran',
      'Jubail': isArabic ? 'الجبيل' : 'Jubail',
      'Jeddah': isArabic ? 'جدة' : 'Jeddah',
      'Qatif': isArabic ? 'القطيف' : 'Qatif',
      'Madinah': isArabic ? 'المدينة' : 'Madinah',
      'Meccah': isArabic ? 'مكه' : 'Meccah',
    };
    
    // Try exact match first
    if (cityMappings[cityName]) {
      return cityMappings[cityName];
    }
    
    // Try normalized match for variations
    const normalizedCity = this.normalize(cityName);
    const canonicalMappings = {
      'riyadh': isArabic ? 'الرياض' : 'Riyadh',
      'al khobar': isArabic ? 'الخبر' : 'Al Khobar',
      'dammam': isArabic ? 'الدمام' : 'Dammam',
      'dhahran': isArabic ? 'الظهران' : 'Dhahran',
      'jubail': isArabic ? 'الجبيل' : 'Jubail',
      'jeddah': isArabic ? 'جدة' : 'Jeddah',
      'qatif': isArabic ? 'القطيف' : 'Qatif',
      'madinah': isArabic ? 'المدينة' : 'Madinah',
      'meccah': isArabic ? 'مكه' : 'meccah',
    };
    
    if (canonicalMappings[normalizedCity]) {
      return canonicalMappings[normalizedCity];
    }
    
    // Fallback to original city name
    return cityName;
  },

  /**
   * Debug function to track city changes
   */
  trackCityChanges() {
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      if (key === 'city' || key === 'city_value') {
        console.trace('City change stack trace:');
      }
      return originalSetItem.call(this, key, value);
    };
    
  }
};

/**
 * WhatsApp function to handle mobile app vs web fallback
 * @param {string} phoneNumber - The phone number to contact
 * @param {string} message - The message to send
 */
function openWhatsApp(phoneNumber, message) {
  // Check if user is on mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Try to open WhatsApp app first
    const appUrl = `whatsapp://send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
    const webUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
    
    // Create a hidden iframe to test if the app opens
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = appUrl;
    document.body.appendChild(iframe);
    
    // Set a timeout to fallback to web if app doesn't open
    setTimeout(() => {
      document.body.removeChild(iframe);
      // If we're still here after 2 seconds, the app probably didn't open
      window.open(webUrl, '_blank');
    }, 2000);
    
    // If the page becomes hidden (app opened), remove the iframe
    const handleVisibilityChange = () => {
      if (document.hidden) {
        document.body.removeChild(iframe);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
  } else {
    // Desktop users go directly to WhatsApp Web
    const webUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
    window.open(webUrl, '_blank');
  }
}